/* A set of photos attached to something — a case study, a social post.
 *
 * One component, because it is one job: add photos, borrow one the gallery
 * already has, decide later which of them belong in an album. The two callers
 * differ only in where the rows live, so they pass that in rather than each
 * growing their own copy of the screen. Two copies of this is how the old
 * case-study and social flows ended up with different bugs.
 *
 * Every photo here is a gallery ASSET. Stored once, public the moment it
 * uploads, and shared rather than copied when it also appears in an album — the
 * split migration 0060 built and nothing used until now.
 *
 * Filing into an album is deliberately per photo and after the fact. A first
 * pass put a "which album?" dropdown above the drop zone, which asks about
 * photos that do not exist yet, of someone looking at an empty box.
 *
 * Everything about one photo — its album, its file name, its caption, its alt
 * text — is in one settings window behind the gear, the way the
 * gallery manager's photo dialog works. A card wearing a button for each of
 * those read as a form, not a set of photographs.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { processImageSized } from '@/lib/image';
import { addAssetToAlbum } from '@/data/client';
import { AlbumSelect } from './AlbumSelect';
import { AssetImg } from './AssetImg';
import { GalleryPhotoPicker } from './GalleryPhotoPicker';
import { Hint } from './Hint';
import { Modal } from './Modal';
import { DragGrip, useMoveOrder } from './MoveButtons';
import { useDragOrder, type DragOrder } from './useDragOrder';

const msg = (e: unknown, fallback: string) =>
  e instanceof Error ? e.message.replace(/^API \d+:\s*/, '') : fallback;

export interface SetPhoto {
  /** The row joining this photo to its owner — what remove() takes. */
  id: string;
  /** The gallery asset. Empty on a legacy case-study photo. */
  assetId: string;
  filename: string;
  cdnUrl: string;
  /** Albums this same photo already appears in, by name. */
  albums: string[];
  /** Case-study photos only: alt text, the caption under it in the grid, and
      the named file in the case study's folder. */
  alt?: string;
  caption?: string;
  publicPath?: string;
  /** Case-study sets only: a video, which plays in place of a thumbnail. */
  isVideo?: boolean;
}

export interface PhotoSetProps {
  heading: string;
  /** One line under the drop zone saying where these photos end up. */
  dropNote: string;
  queryKey: unknown[];
  load: () => Promise<{ photos: SetPhoto[]; mainId: string }>;
  upload: (img: { file: File; width: number; height: number }) => Promise<unknown>;
  addExisting: (assetId: string) => Promise<unknown>;
  remove: (rowId: string) => Promise<unknown>;
  /** Omit where the idea does not apply — a social post has an order, not a hero. */
  setMain?: (rowId: string) => Promise<unknown>;
  /** Save the running order, whole. */
  reorder: (ids: string[]) => Promise<unknown>;
  /** Name the photo's file and set its alt text and caption. Omit where photos have no file of their own. */
  editFile?: (rowId: string, input: { name?: string; alt?: string; caption?: string }) => Promise<unknown>;
  /** After any change lands — for screens that show something derived from these
      photos (a case study's HTML ends with their grid). */
  onChanged?: () => void;
  emptyNote?: string;
  /** Take videos as well as photos. Only where the server does (a case study's
      photos); a social post's set stays photos only. */
  acceptVideo?: boolean;
}

/** The most one upload can be — the Worker's request body limit. */
const MAX_VIDEO_MB = 100;
const isVideoFile = (f: File) => f.type.startsWith('video/') || /\.(mp4|m4v|mov|webm)$/i.test(f.name);

export function PhotoSet(props: PhotoSetProps) {
  const { heading, dropNote, queryKey, load, upload, addExisting, remove, setMain, reorder } = props;
  const qc = useQueryClient();
  /* A case-study or interview photo gets its caption, alt text and file name from AI a
     few seconds AFTER it uploads (describeNewPhoto, in the background). Until
     then this keeps asking, so the words appear without a reload; it stops once
     every photo has alt text or after two minutes, whichever is first. */
  const [describingUntil, setDescribingUntil] = useState(0);
  // Videos are left out: AI writes for photos only. Alt and caption arrive together.
  const describing = (list: SetPhoto[] | undefined) =>
    !!props.editFile && describingUntil > Date.now() && (list ?? []).some((p) => !p.isVideo && !(p.alt || '').trim());
  const { data } = useQuery({
    queryKey, queryFn: load,
    refetchInterval: (q) => (describing(q.state.data?.photos) ? 4000 : false),
  });
  const startDescribing = () => { if (props.editFile) setDescribingUntil(Date.now() + 120_000); };

  const [picking, setPicking] = useState(false);
  const [settingsId, setSettingsId] = useState('');
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false); // a file is being dragged over the zone
  const [err, setErr] = useState('');
  const inRef = useRef<HTMLInputElement>(null);

  const photos = data?.photos ?? [];
  /* Nobody having chosen does not mean there is no main image. With no hero set,
     getProjectImages returns the photos in order and every consumer treats the
     first as the lead — the frontmatter's featured_image, the HTML template's
     one figure, the DNN hero. So the first photo IS the main image until someone
     says otherwise, and showing it as such is reporting what will happen rather
     than pretending the question is open. */
  const mainId = data?.mainId || photos.find((p) => !p.isVideo)?.id || '';
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey });
    props.onChanged?.();
  };
  // New alt text renames the file and rewrites the case study's grid, so the
  // screens showing those are told when some arrives.
  const described = photos.filter((p) => (p.alt || '').trim()).length;
  const lastDescribed = useRef(described);
  useEffect(() => {
    if (described > lastDescribed.current && describingUntil) props.onChanged?.();
    lastDescribed.current = described;
  }, [described]); // only a change in the count matters

  const addFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    /* Dropping a folder of photos as a .zip is the usual way this goes wrong.
       The server refuses anything that is not an image too; saying so here
       means the photos beside it in the same drop still go up. */
    const all = Array.from(files);
    const isImage = (f: File) => f.type.startsWith('image/') || /\.(jpe?g|png|webp|gif|heic|heif|avif)$/i.test(f.name);
    const tooBig = props.acceptVideo ? all.filter((f) => isVideoFile(f) && f.size > MAX_VIDEO_MB * 1024 * 1024) : [];
    const images = all.filter((f) => isImage(f) || (props.acceptVideo && isVideoFile(f) && !tooBig.includes(f)));
    const refused = all.filter((f) => !images.includes(f) && !tooBig.includes(f));
    setErr(tooBig.length
      ? `Over ${MAX_VIDEO_MB} MB, so not uploaded: ${tooBig.map((f) => f.name).join(', ')}. Trim the video or export it smaller.`
      : refused.length
        ? (refused.some((f) => /zip/i.test(f.type) || /\.zip$/i.test(f.name))
            ? 'Zip folders can’t be uploaded. Unzip it and add the photos themselves.'
            : `${props.acceptVideo ? 'Not a photo or video' : 'Not a photo'}: ${refused.map((f) => f.name).join(', ')}.`)
        : '');
    if (!images.length) { if (inRef.current) inRef.current.value = ''; return; }
    setBusy(true);
    try {
      // A video goes up as it is: the canvas pass that shrinks photos cannot read one.
      for (const f of images) await upload(isVideoFile(f) ? { file: f, width: 0, height: 0 } : await processImageSized(f));
      await qc.invalidateQueries({ queryKey: ['galleryAssets'] });
      startDescribing();
      refresh();
    } catch (e) { setErr(msg(e, 'Upload failed.')); }
    finally { setBusy(false); if (inRef.current) inRef.current.value = ''; }
  };

  const fromGallery = useMutation({
    mutationFn: addExisting,
    onSuccess: () => { setPicking(false); startDescribing(); refresh(); },
    onError: (e) => { setPicking(false); setErr(msg(e, 'Could not add that photo.')); },
  });
  const main = useMutation({
    mutationFn: (rowId: string) => setMain!(rowId),
    onSuccess: () => refresh(),
    onError: (e) => setErr(msg(e, 'Could not set the main image.')),
  });
  const drop = useMutation({
    mutationFn: remove,
    onSuccess: () => refresh(),
    onError: (e) => setErr(msg(e, 'Could not remove it.')),
  });

  /* Order is not decoration here. With no main image chosen the first photo is
     the one that leads the piece, and on a social post the first four are the
     ones that go out. */
  const keys = photos.map((p) => p.id);
  const order = useMoveOrder(keys, reorder, { onSaved: refresh, onError: setErr });
  // Dragging and the arrow keys on the grip save through the same route.
  const drag = useDragOrder(keys, reorder, { onSaved: refresh, onError: setErr });
  const byId = new Map(photos.map((p) => [p.id, p]));
  const shown = drag.ids.map((k) => byId.get(k)).filter((p): p is SetPhoto => !!p);
  const canOrder = keys.length > 1;
  const settings = settingsId ? photos.find((p) => p.id === settingsId) : undefined;

  return (
    <div className="es-photos">
      <div className="es-listlabel">
        <Icon name="image" size={14} />
        <span>{heading}</span>
        <span className="es-listlabel__count">{photos.length}</span>
      </div>

      {err && <p className="vault-err"><Icon name="alert-circle" size={13} /> {err}</p>}

      {/* Drop files from Finder / Explorer as well as clicking. The zone is a
          plain div, so without these handlers the browser's default takes
          over and a dropped image simply opens in the tab. */}
      <div className={`es-drop${over ? ' es-drop--over' : ''}`} onClick={() => inRef.current?.click()}
        onDragEnter={(e) => { e.preventDefault(); setOver(true); }}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; if (!over) setOver(true); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false); }}
        onDrop={(e) => { e.preventDefault(); setOver(false); void addFiles(e.dataTransfer.files); }}>
        <span className="es-drop__ic"><Icon name="upload" size={20} /></span>
        <strong>{busy ? 'Uploading…' : over ? 'Drop to add' : 'Click or drag photos here'}</strong>
        <span>{dropNote}</span>
        <input ref={inRef} type="file" accept={props.acceptVideo ? 'image/*,video/*' : 'image/*'} multiple hidden
          onChange={(e) => void addFiles(e.target.files)} />
      </div>

      {/* Under the drop zone, not beside it: the second way to do the same
          thing, which floating alongside made read as unrelated. */}
      <div className="es-photos__alt">
        <span>or</span>
        <Button variant="ghost" size="sm" iconLeft={<Icon name="image" size={15} />}
          onClick={() => { setPicking(true); setErr(''); }}>
          Add a photo from your gallery
        </Button>
      </div>

      {picking && (
        <GalleryPhotoPicker
          taken={photos.map((p) => p.assetId).filter(Boolean)}
          onPick={(assetId) => fromGallery.mutate(assetId)}
          onClose={() => setPicking(false)} />
      )}

      <div className={`es-photogrid${drag.draggingId ? ' is-dragging' : ''}`}>
        {shown.map((p) => (
          <Single key={p.id} id={p.id} drag={drag}>
            <PhotoCard p={p} isMain={p.id === mainId} writing={describing(photos)}
              onMain={setMain && !p.isVideo ? () => main.mutate(p.id) : undefined}
              onSettings={() => { setSettingsId(p.id); setErr(''); }}
              onRemove={() => {
                if (!confirm('Remove this photo?')) return;
                setErr(''); drop.mutate(p.id);
              }} />
            {canOrder && <DragGrip id={p.id} drag={drag} keys={order} label="photo" className="es-grip es-grip--float" />}
          </Single>
        ))}
      </div>

      {settings && (
        <PhotoSettings photo={settings} editFile={props.editFile}
          onClose={() => setSettingsId('')}
          onSaved={() => { void refresh(); }} />
      )}

      {!photos.length && <p className="es-hint">{props.emptyNote ?? 'No photos yet.'}</p>}
    </div>
  );
}

/* A single photo's slot in the grid: what the drag measures and holds. */
function Single({ id, drag, children }: { id: string; drag: DragOrder; children: ReactNode }) {
  return (
    <div ref={(el) => drag.register(id, el)} className={`es-photoslot${drag.draggingId === id ? ' is-held' : ''}`}>
      {children}
    </div>
  );
}

function PhotoCard({ p, isMain, writing, onMain, onSettings, onRemove }: {
  p: SetPhoto; isMain: boolean; writing?: boolean;
  onMain?: () => void; onSettings: () => void; onRemove: () => void;
}) {
  return (
    <figure className={`es-photocard${isMain ? ' is-hero' : ''}`}>
      {p.isVideo ? (
        /* The first frame, with the controls to play it here. An iPhone .MOV is
           often HEVC, which Chrome on Windows and Firefox may not play; the file
           is still on the site as it was uploaded. */
        <video className="es-photocard__img" src={`${p.cdnUrl}#t=0.1`} muted playsInline controls preload="metadata"
          title={/\.mov$/i.test(p.cdnUrl) ? 'An iPhone .MOV may not play in every browser. An MP4 plays everywhere.' : undefined} />
      ) : (
        <AssetImg url={p.cdnUrl} assetId={p.assetId} alt={p.alt || p.filename} className="es-photocard__img" />
      )}
      {p.isVideo && <span className="es-photocard__role">Video</span>}

      {/* State and its one action share a spot, so the eye learns to look in
          the same place on every card: this photo is the main image, or the
          button that makes it so. */}
      {onMain && (isMain ? (
        <span className="es-photocard__kicker">Main image</span>
      ) : (
        <button type="button" className="es-photocard__kicker es-photocard__kicker--set"
          title="Use as the main image" onClick={onMain}>
          Make main image
        </button>
      ))}

      {/* The caption is what readers see, so it is what the card shows; alt
          text stands in until a photo has one. */}
      <figcaption>
        <span className="es-photocard__name" title={p.caption || p.alt || p.publicPath || p.filename}>
          {p.albums.length ? <Icon name="layers" size={11} /> : null}
          {p.caption || p.alt || (writing && !p.isVideo ? 'Writing caption…' : p.publicPath ? p.publicPath.split('/').pop() : p.isVideo ? 'Video' : 'No caption yet')}
        </span>
        <span className="es-photocard__acts">
          <button type="button" className="es-photocard__gear" title="Photo settings: album, file name, caption, alt text"
            aria-label="Photo settings" onClick={onSettings}>
            <Icon name="settings" size={14} />
          </button>
          <button type="button" className="es-row__del" title="Remove" onClick={onRemove}>
            <Icon name="trash" size={14} />
          </button>
        </span>
      </figcaption>

      {/* A legacy case-study photo has no gallery asset behind it and still
          needs Triton to publish it before anything can use it. */}
      {!p.assetId && !p.cdnUrl && (
        <span className="es-photocard__note">Waiting on Triton to publish</span>
      )}
    </figure>
  );
}

/* A field whose explanation sits behind an "i" beside its label rather than
   under the box. A div, not a <label>: a label holding the hint's button and
   the input would label the button, so clicking the label text would toggle
   the hint instead of focusing the box. */
function TipField({ label, tip, children }: { label: string; tip: ReactNode; children: ReactNode }) {
  return (
    <div className="tr-field">
      <span className="tr-field__label es-tiplabel">{label}<Hint label={`About ${label.toLowerCase()}`}>{tip}</Hint></span>
      {children}
    </div>
  );
}

/* One photo, opened: its album, file name, caption and alt text.
 *
 * Save writes the name only when it changed: every rename writes a new file
 * (the old one stays, because a page already on the client's site may link
 * it), so saving just the caption must not mint one. */
export function PhotoSettings({ photo, editFile, onClose, onSaved }: {
  photo: SetPhoto;
  editFile?: PhotoSetProps['editFile'];
  onClose: () => void; onSaved: () => void;
}) {
  const path = photo.publicPath || '';
  const current = path ? path.slice(path.lastIndexOf('/') + 1).replace(/\.[^.]+$/, '') : '';
  const folder = path ? path.slice(0, path.lastIndexOf('/') + 1) : '';
  const ext = path.includes('.') ? path.slice(path.lastIndexOf('.')) : '';
  const [name, setName] = useState(current);
  const [alt, setAlt] = useState(photo.alt || '');
  const [caption, setCaption] = useState(photo.caption || '');
  const [albumId, setAlbumId] = useState('');
  const [err, setErr] = useState('');

  const save = useMutation({
    mutationFn: async () => {
      const input = {
        ...(editFile && name.trim() && name.trim() !== current ? { name: name.trim() } : {}),
        ...(editFile && alt !== (photo.alt || '') ? { alt } : {}),
        ...(editFile && caption !== (photo.caption || '') ? { caption } : {}),
      };
      if (Object.keys(input).length) await editFile!(photo.id, input);
      if (albumId && photo.assetId) await addAssetToAlbum(albumId, photo.assetId);
    },
    onSuccess: () => { onSaved(); onClose(); },
    onError: (e) => setErr(msg(e, 'Could not save that.')),
  });

  return (
    <Modal title="Photo settings" onClose={onClose}>
      <div className="es-modal__body">
        <AssetImg url={photo.cdnUrl} assetId={photo.assetId} alt={photo.alt || photo.filename} className="es-photoset__preview" />

        {editFile && (
          <>
            <TipField label="File name" tip={path
              ? `Now ${folder}${current}${ext}. Lowercase and hyphens are applied for you; the old file stays online for pages that already use it.`
              : 'It goes in this case study’s folder. Left empty, it is named after the case study when it is first pushed to DNN.'}>
              <Input aria-label="File name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder={current || 'e.g. cedar-siding-removal'} />
            </TipField>
            <TipField label="Caption" tip="Shown under the photo when someone opens it full-screen from the DNN article’s photo grid. Written by AI from the photo and the interview; edit it freely, or clear it to show none.">
              <Input aria-label="Caption" value={caption} onChange={(e) => setCaption(e.target.value)}
                placeholder="e.g. Rotted sill framing found once the old window came out" />
            </TipField>
            <TipField label="Alt text" tip="Not shown on the page; read by screen readers and search engines.">
              <Input aria-label="Alt text" value={alt} onChange={(e) => setAlt(e.target.value)}
                placeholder="What the photo shows, for screen readers and search" />
            </TipField>
          </>
        )}

        <span className="es-label">Album</span>
        {photo.assetId ? (
          <>
            {!!photo.albums.length && (
              <span className="es-hint">
                Already in {photo.albums.join(', ')}. Adding it to another album puts the same photo in both — it is not a copy.
              </span>
            )}
            <AlbumSelect value={albumId} onChange={setAlbumId}
              placeholder={photo.albums.length ? 'Add it to another album…' : 'Add it to an album…'} />
          </>
        ) : (
          <span className="es-hint">
            This photo was uploaded before galleries and case studies shared their photos, so it can’t be filed yet.
            Triton can publish it, or you can upload it again here and it will be shareable.
          </span>
        )}

        {err && <p className="vault-err"><Icon name="alert-circle" size={13} /> {err}</p>}
      </div>
      <div className="es-modal__foot">
        <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
        <Button variant="primary" size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Modal>
  );
}
